import { useCallback, useEffect, useMemo, useState } from 'react';
import apiClient from '@src/api/clients';

/**
 * Fetches parent companies by querying the clients endpoint for unique
 * parent_company / parent_company_name pairs and deduplicating.
 */
const useParentCompanyAutocomplete = ({ skip = false } = {}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchParentCompanies = useCallback(async () => {
    if (skip) return;
    setLoading(true);
    try {
      const collected = new Map();
      let url = 'dashboard/clients/';
      let isFirst = true;
      let fetches = 0;

      while (url && fetches < 20) {
        const config = { url, method: 'GET' };
        if (isFirst) {
          config.payload = {
            fields: 'parent_company,parent_company_name',
            page: 1,
            page_size: 200,
            active: 'true',
          };
        }

        const res = await apiClient.request(config);
        const results = res?.data?.results || res?.data || [];
        results.forEach((c) => {
          if (c.parent_company && c.parent_company_name) {
            collected.set(String(c.parent_company), c.parent_company_name);
          }
        });

        url = res?.data?.next || null;
        isFirst = false;
        fetches += 1;
      }

      const sorted = Array.from(collected.entries())
        .map(([value, label]) => ({ value: Number(value), label }))
        .sort((a, b) => a.label.localeCompare(b.label));

      setOptions(sorted);
    } catch (err) {
      console.error('Failed to fetch parent companies', err);
    } finally {
      setLoading(false);
    }
  }, [skip]);

  useEffect(() => {
    fetchParentCompanies();
  }, [fetchParentCompanies]);

  const findOptionByValue = useCallback(
    (value) => {
      if (value === null || value === undefined || value === '') return null;
      const target = String(value);
      return options.find((o) => String(o.value) === target) || null;
    },
    [options],
  );

  const memoizedOptions = useMemo(() => options, [options]);

  return {
    options: memoizedOptions,
    loading,
    findOptionByValue,
  };
};

export default useParentCompanyAutocomplete;
